import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import api, { type MilestoneApp } from 'api';
import { MilestonePayload } from 'api/models/payload/MilestonesPayload';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import { currencyOptions } from 'utils/currencyOptions';
import { downloadBlob } from 'utils/file';
import { getErrorMessage } from 'utils/v2/errorHelper';

type MilestoneInvoiceAction = 'view' | 'download' | 'send';
type MilestoneStatusField = 'status' | 'payment_status';
type MilestoneEditableField = 'name' | 'description' | 'price';

interface PendingInvoiceAction {
    milestoneId: number;
    action: MilestoneInvoiceAction;
}

interface StatusUpdateState {
    milestoneId: number;
    field: MilestoneStatusField;
}

const NEW_MILESTONE_ID_BASE = -1;

export interface InvoicePreviewState {
    file: Blob;
    milestoneId: number;
}

interface UseApplicationMilestonesEditorArgs {
    appId: string;
    /** Legacy PDF flow re-issues the offer email; template retainers pass `false`. */
    notifyClient?: boolean;
}

const createBlankMilestone = (appId: string, currency: string, index: number): MilestoneApp => ({
    id: NEW_MILESTONE_ID_BASE - index,
    application_id: Number(appId),
    created_at: new Date().toISOString(),
    currency,
    hubspot_id: null,
    description: '',
    name: '',
    price: '',
    status: 'PENDING',
    payment_id: null,
    payment_status: 'PENDING',
});

/**
 * Case-offer milestones: inline edit, status PATCHes, and invoice actions.
 * Same save path as the case-milestones dialog (bulk POST replaces the set).
 */
export const useApplicationMilestonesEditor = ({
    appId,
    notifyClient = false,
}: UseApplicationMilestonesEditorArgs) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();

    const [milestones, setMilestones] = useState<MilestoneApp[]>([]);
    const [savedMilestones, setSavedMilestones] = useState<MilestoneApp[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currency, setCurrency] = useState(currencyOptions[0]?.value ?? 'USD');
    const [pendingAction, setPendingAction] = useState<PendingInvoiceAction | null>(null);
    const [statusUpdating, setStatusUpdating] = useState<StatusUpdateState | null>(null);
    const [invoicePreview, setInvoicePreview] = useState<InvoicePreviewState | null>(null);

    const showError = useCallback(
        (error: unknown) => {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        },
        [dispatch, translate]
    );

    const loadMilestones = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.application.milestones.get(appId);
            const data = response.data ?? [];
            setMilestones(data);
            setSavedMilestones(data);
            if (data.length > 0 && data[0].currency) {
                setCurrency(data[0].currency);
            }
        } catch (error) {
            showError(error);
            setMilestones([]);
            setSavedMilestones([]);
        } finally {
            setLoading(false);
        }
    }, [appId, showError]);

    useEffect(() => {
        setEditMode(false);
        setInvoicePreview(null);
        void loadMilestones();
    }, [loadMilestones]);

    const fetchInvoiceFile = async (milestoneId: number): Promise<Blob> => {
        const response = await api.application.getMilestoneInvoice(appId, milestoneId.toString());
        return new Blob([response.data], { type: 'application/pdf' });
    };

    const handleInvoiceAction = async (action: MilestoneInvoiceAction, milestone: MilestoneApp) => {
        setPendingAction({ milestoneId: milestone.id, action });
        try {
            if (action === 'view') {
                const file = await fetchInvoiceFile(milestone.id);
                setInvoicePreview({ file, milestoneId: milestone.id });
            } else if (action === 'download') {
                const file = await fetchInvoiceFile(milestone.id);
                downloadBlob(file, `invoice-${milestone.id}.pdf`);
            } else {
                await api.application.sendInvoiceEmail(appId, milestone.id.toString());
                dispatch(
                    setAlert({ message: translate('emailSuccessfullySent'), type: 'success' })
                );
            }
        } catch (error) {
            showError(error);
        } finally {
            setPendingAction(null);
        }
    };

    const handleStatusChange = async (
        milestone: MilestoneApp,
        field: MilestoneStatusField,
        value: string
    ) => {
        if (milestone.id < 0) {
            setMilestones((prev) =>
                prev.map((item) => (item.id === milestone.id ? { ...item, [field]: value } : item))
            );
            return;
        }
        setStatusUpdating({ milestoneId: milestone.id, field });
        try {
            if (field === 'payment_status') {
                await api.application.editMilestonesPaymentStatus(
                    appId,
                    milestone.id.toString(),
                    value
                );
            } else {
                await api.application.editMilestoneStatus(appId, milestone.id.toString(), value);
            }
            const apply = (item: MilestoneApp) =>
                item.id === milestone.id ? { ...item, [field]: value } : item;
            setMilestones((prev) => prev.map(apply));
            setSavedMilestones((prev) => prev.map(apply));
            dispatch(setAlert({ message: translate('statusUpdated'), type: 'success' }));
        } catch (error) {
            showError(error);
        } finally {
            setStatusUpdating(null);
        }
    };

    const handleMilestoneFieldChange = (
        milestone: MilestoneApp,
        field: MilestoneEditableField,
        value: string
    ) => {
        if (field === 'price' && value !== '' && Number.isNaN(Number(value))) return;
        setMilestones((prev) =>
            prev.map((item) => (item.id === milestone.id ? { ...item, [field]: value } : item))
        );
    };

    const handleDeleteMilestone = (milestone: MilestoneApp) => {
        setMilestones((prev) => prev.filter((item) => item.id !== milestone.id));
    };

    const addMilestone = () => {
        setMilestones((prev) => [...prev, createBlankMilestone(appId, currency, prev.length)]);
    };

    const startEdit = () => {
        setEditMode(true);
        setMilestones((prev) =>
            prev.length === 0 ? [createBlankMilestone(appId, currency, 0)] : prev
        );
    };

    const cancelEdit = () => {
        setMilestones(savedMilestones);
        setCurrency(savedMilestones[0]?.currency || 'USD');
        setEditMode(false);
    };

    const hasEditErrors = milestones.some(
        (milestone) =>
            !milestone.name.trim() ||
            !milestone.description.trim() ||
            !milestone.price.toString().trim()
    );

    const save = async () => {
        if (!milestones.length || hasEditErrors) return;

        setSaving(true);
        try {
            const payload: MilestonePayload[] = milestones.map((item) => ({
                ...(item.id > 0 ? { id: item.id } : {}),
                name: item.name.trim(),
                description: item.description.trim(),
                price: item.price.toString(),
                currency,
            }));
            await api.application.milestones.post(
                appId,
                payload,
                notifyClient ? { params: { notifyClient: true } } : undefined
            );
            const response = await api.application.milestones.get(appId);
            const data = response.data ?? [];
            setMilestones(data);
            setSavedMilestones(data);
            setEditMode(false);
            dispatch(
                setAlert({
                    message: translate(notifyClient ? 'offerSent' : 'offerSaved'),
                    type: 'success',
                })
            );
        } catch (error) {
            showError(error);
        } finally {
            setSaving(false);
        }
    };

    return {
        milestones,
        loading,
        saving,
        editMode,
        currency,
        setCurrency,
        hasEditErrors,
        statusUpdating,
        pendingAction,
        invoicePreview,
        closeInvoicePreview: () => setInvoicePreview(null),
        startEdit,
        cancelEdit,
        addMilestone,
        save,
        handleInvoiceAction,
        handleStatusChange,
        handleMilestoneFieldChange,
        handleDeleteMilestone,
        reload: loadMilestones,
    };
};

export type ApplicationMilestonesEditor = ReturnType<typeof useApplicationMilestonesEditor>;
